import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * ThemeContext — 1:1 port of iOS `mobile/src/context/ThemeContext.tsx`.
 *
 *   • `mode` is the user preference ("system" | "light" | "dark") persisted
 *     to `localStorage.brontoy_theme_mode`.
 *   • `theme` is the *resolved* value the app actually renders in ("light" or
 *     "dark").  When mode = "system" we mirror `prefers-color-scheme`
 *     changes live via the media-query listener.
 *   • `setMode(next)` updates both, `toggle()` flips between light/dark
 *     directly (keeps the existing top-bar sun/moon button working).
 */

const ThemeContext = createContext(null);

const STORAGE_KEY = "brontoy_theme_mode";
// Legacy key from the pre-spec ThemeContext.  We read it once for migration.
const LEGACY_KEY = "brontoy_theme";

// Device-specific default: desktop (≥ 768 px) opens in DARK mode; phone
// opens in LIGHT.  Only applies when the user has NOT chosen a
// preference yet — once they toggle or open Settings, their choice
// wins forever.
function deviceDefaultMode() {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(min-width: 768px)").matches ? "dark" : "light";
}

function readInitialMode() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "system" || raw === "light" || raw === "dark") return raw;
    // Migrate the old "brontoy_theme" ("light" | "dark") key over.
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy === "light" || legacy === "dark") return legacy;
  } catch {
    /* localStorage unavailable */
  }
  // First-run default — desktop → dark, phone → light.
  return deviceDefaultMode();
}

function systemPrefersDark() {
  if (typeof window === "undefined" || !window.matchMedia) return true;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(readInitialMode);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // Watch OS setting when in system mode.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setSystemDark(e.matches);
    // addEventListener isn't supported on the mq object in very old Safari.
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, []);

  const theme = mode === "system" ? (systemDark ? "dark" : "light") : mode;

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
  }, [theme]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, [mode]);

  const toggle = useCallback(() => {
    // Toggling only flips light↔dark; users have to open Settings to pick
    // "System".  Matches iOS behaviour of the top-bar sun/moon button.
    setMode((current) => {
      const resolved = current === "system" ? (systemDark ? "dark" : "light") : current;
      return resolved === "dark" ? "light" : "dark";
    });
  }, [systemDark]);

  const value = useMemo(
    () => ({ theme, mode, setMode, setTheme: setMode, toggle }),
    [theme, mode, toggle]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
